if (!customElements.get('tooto-image-preview')) {
    class TootoImagePreview extends HTMLElement {
        constructor() {
            super();
            this.scale = 1;
            this.translateX = 0;
            this.translateY = 0;
            this.maxScale = 4;
            this.pointers = new Map();
            this.pinchState = null;
            this.scrollPosition = 0;

            this.handleOpenClick = this.openPreview.bind(this);
            this.handleCloseClick = this.closePreview.bind(this);
            this.handleZoomIn = () => this.changeScale(this.scale + 0.5);
            this.handleZoomOut = () => this.changeScale(this.scale - 0.5);
            this.handleReset = this.resetZoom.bind(this);
            this.handlePointerDown = this.onPointerDown.bind(this);
            this.handlePointerMove = this.onPointerMove.bind(this);
            this.handlePointerEnd = this.onPointerEnd.bind(this);
            this.handleKeydown = this.onKeydown.bind(this);
            this.handleResize = this.onResize.bind(this);
            this.handleMediaChange = this.syncMobileState.bind(this);
            this.handleImageLoad = this.onImageLoad.bind(this);
        }

        connectedCallback() {
            if (this.initialized) return;

            this.openButton = this.querySelector('[data-image-preview-open]');
            this.modal = this.querySelector('[data-image-preview-modal]');
            this.closeButton = this.querySelector('[data-image-preview-close]');
            this.stage = this.querySelector('[data-image-preview-stage]');
            this.image = this.querySelector('[data-image-preview-image]');
            this.zoomInButton = this.querySelector('[data-image-preview-zoom-in]');
            this.zoomOutButton = this.querySelector('[data-image-preview-zoom-out]');
            this.resetButton = this.querySelector('[data-image-preview-reset]');
            this.zoomValue = this.querySelector('[data-image-preview-zoom-value]');

            if (!this.openButton || !this.modal || !this.closeButton || !this.stage || !this.image) return;

            this.initialized = true;
            this.mobileQuery = window.matchMedia('(max-width: 767px)');

            this.openButton.addEventListener('click', this.handleOpenClick);
            this.closeButton.addEventListener('click', this.handleCloseClick);
            this.zoomInButton?.addEventListener('click', this.handleZoomIn);
            this.zoomOutButton?.addEventListener('click', this.handleZoomOut);
            this.resetButton?.addEventListener('click', this.handleReset);
            this.stage.addEventListener('pointerdown', this.handlePointerDown);
            this.stage.addEventListener('pointermove', this.handlePointerMove, { passive: false });
            this.stage.addEventListener('pointerup', this.handlePointerEnd);
            this.stage.addEventListener('pointercancel', this.handlePointerEnd);
            this.image.addEventListener('load', this.handleImageLoad);
            document.addEventListener('keydown', this.handleKeydown);
            window.addEventListener('resize', this.handleResize);

            if (this.mobileQuery.addEventListener) {
                this.mobileQuery.addEventListener('change', this.handleMediaChange);
            } else {
                this.mobileQuery.addListener(this.handleMediaChange);
            }

            this.syncMobileState();
            this.updateTransform();
        }

        disconnectedCallback() {
            if (!this.initialized) return;

            if (this.hasAttribute('open')) this.closePreview(false);
            this.openButton.removeEventListener('click', this.handleOpenClick);
            this.closeButton.removeEventListener('click', this.handleCloseClick);
            this.zoomInButton?.removeEventListener('click', this.handleZoomIn);
            this.zoomOutButton?.removeEventListener('click', this.handleZoomOut);
            this.resetButton?.removeEventListener('click', this.handleReset);
            this.stage.removeEventListener('pointerdown', this.handlePointerDown);
            this.stage.removeEventListener('pointermove', this.handlePointerMove);
            this.stage.removeEventListener('pointerup', this.handlePointerEnd);
            this.stage.removeEventListener('pointercancel', this.handlePointerEnd);
            this.image.removeEventListener('load', this.handleImageLoad);
            document.removeEventListener('keydown', this.handleKeydown);
            window.removeEventListener('resize', this.handleResize);

            if (this.mobileQuery.addEventListener) {
                this.mobileQuery.removeEventListener('change', this.handleMediaChange);
            } else {
                this.mobileQuery.removeListener(this.handleMediaChange);
            }

            this.initialized = false;
        }

        syncMobileState() {
            const mobileEnabled = this.mobileQuery.matches;
            this.openButton.disabled = !mobileEnabled;
            this.openButton.setAttribute('aria-disabled', mobileEnabled ? 'false' : 'true');

            if (!mobileEnabled && this.hasAttribute('open')) {
                this.closePreview(false);
            }
        }

        openPreview() {
            if (!this.mobileQuery.matches || this.hasAttribute('open')) return;

            document.querySelectorAll('tooto-image-preview[open]').forEach((preview) => {
                if (preview !== this && typeof preview.closePreview === 'function') preview.closePreview(false);
            });

            this.lastFocusedElement = document.activeElement;
            this.modalReturnParent = this.modal.parentNode;
            this.modalReturnNextSibling = this.modal.nextSibling;
            document.body.appendChild(this.modal);
            this.modal.hidden = false;
            this.setAttribute('open', '');
            this.lockPageScroll();
            this.sizePreviewImage();
            this.image.src = this.image.dataset.previewSrc || this.image.src;
            this.resetZoom();

            requestAnimationFrame(() => this.closeButton.focus());
        }

        closePreview(restoreFocus = true) {
            if (!this.hasAttribute('open')) return;

            this.pointers.clear();
            this.pinchState = null;
            this.stage.classList.remove('is-gesturing');
            this.removeAttribute('open');
            this.modal.hidden = true;
            this.unlockPageScroll();
            this.resetZoom();

            if (this.modalReturnParent) {
                if (
                    this.modalReturnNextSibling &&
                    this.modalReturnNextSibling.parentNode === this.modalReturnParent
                ) {
                    this.modalReturnParent.insertBefore(this.modal, this.modalReturnNextSibling);
                } else {
                    this.modalReturnParent.appendChild(this.modal);
                }
            }

            if (restoreFocus && this.lastFocusedElement instanceof HTMLElement) {
                this.lastFocusedElement.focus();
            }
        }

        lockPageScroll() {
            this.scrollPosition = window.scrollY;
            document.documentElement.classList.add('tooto-image-preview-is-open');
            document.body.style.setProperty('--tooto-image-preview-scroll-offset', `-${this.scrollPosition}px`);
            document.body.classList.add('tooto-image-preview-is-open');
        }

        unlockPageScroll() {
            document.documentElement.classList.remove('tooto-image-preview-is-open');
            document.body.classList.remove('tooto-image-preview-is-open');
            document.body.style.removeProperty('--tooto-image-preview-scroll-offset');
            window.scrollTo(0, this.scrollPosition);
        }

        resetZoom() {
            this.scale = 1;
            this.translateX = 0;
            this.translateY = 0;
            this.updateTransform();
        }

        changeScale(nextScale) {
            const clampedScale = Math.min(this.maxScale, Math.max(1, nextScale));
            const scaleRatio = clampedScale / this.scale;

            this.translateX *= scaleRatio;
            this.translateY *= scaleRatio;
            this.scale = clampedScale;

            if (this.scale === 1) {
                this.translateX = 0;
                this.translateY = 0;
            }

            this.clampTranslation();
            this.updateTransform();
        }

        getStageMetrics() {
            const rect = this.stage.getBoundingClientRect();
            const styles = window.getComputedStyle(this.stage);
            const paddingTop = parseFloat(styles.paddingTop) || 0;
            const paddingRight = parseFloat(styles.paddingRight) || 0;
            const paddingBottom = parseFloat(styles.paddingBottom) || 0;
            const paddingLeft = parseFloat(styles.paddingLeft) || 0;
            const width = Math.max(0, rect.width - paddingLeft - paddingRight);
            const height = Math.max(0, rect.height - paddingTop - paddingBottom);

            return {
                centerX: rect.left + paddingLeft + width / 2,
                centerY: rect.top + paddingTop + height / 2,
                width,
                height
            };
        }

        sizePreviewImage() {
            const metrics = this.getStageMetrics();
            const widthAttribute = parseFloat(this.image.getAttribute('width'));
            const heightAttribute = parseFloat(this.image.getAttribute('height'));
            const configuredRatio = parseFloat(this.image.dataset.previewAspectRatio);
            const fallbackRatio = widthAttribute > 0 && heightAttribute > 0 ? widthAttribute / heightAttribute : 0;
            const aspectRatio = configuredRatio > 0 ? configuredRatio : fallbackRatio;

            if (!(aspectRatio > 0) || !(metrics.width > 0) || !(metrics.height > 0)) return;

            let width = metrics.width;
            let height = width / aspectRatio;

            if (height > metrics.height) {
                height = metrics.height;
                width = height * aspectRatio;
            }

            this.image.style.width = `${Math.round(width)}px`;
            this.image.style.height = `${Math.round(height)}px`;
        }

        clampTranslation() {
            const metrics = this.getStageMetrics();
            const maxX = Math.max(0, (this.image.clientWidth * this.scale - metrics.width) / 2);
            const maxY = Math.max(0, (this.image.clientHeight * this.scale - metrics.height) / 2);

            this.translateX = Math.min(maxX, Math.max(-maxX, this.translateX));
            this.translateY = Math.min(maxY, Math.max(-maxY, this.translateY));
        }

        updateTransform() {
            this.image.style.transform = `translate3d(${this.translateX}px, ${this.translateY}px, 0) scale(${this.scale})`;

            if (this.zoomValue) this.zoomValue.textContent = `${Math.round(this.scale * 100)}%`;
            if (this.zoomOutButton) this.zoomOutButton.disabled = this.scale <= 1;
            if (this.zoomInButton) this.zoomInButton.disabled = this.scale >= this.maxScale;
        }

        startPinch() {
            const points = Array.from(this.pointers.values()).slice(0, 2);
            if (points.length < 2) return;

            const centerX = (points[0].x + points[1].x) / 2;
            const centerY = (points[0].y + points[1].y) / 2;
            const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
            const metrics = this.getStageMetrics();

            this.pinchState = {
                distance: Math.max(1, distance),
                scale: this.scale,
                contentX: (centerX - metrics.centerX - this.translateX) / this.scale,
                contentY: (centerY - metrics.centerY - this.translateY) / this.scale
            };
        }

        onPointerDown(event) {
            if (event.pointerType === 'mouse' && event.button !== 0) return;

            event.preventDefault();
            this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            this.stage.classList.add('is-gesturing');

            try {
                this.stage.setPointerCapture(event.pointerId);
            } catch (error) {
                // Pointer capture is optional; gestures still work without it.
            }

            if (this.pointers.size >= 2) this.startPinch();
        }

        onPointerMove(event) {
            const previousPoint = this.pointers.get(event.pointerId);
            if (!previousPoint) return;

            event.preventDefault();
            this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

            if (this.pointers.size >= 2 && this.pinchState) {
                const points = Array.from(this.pointers.values()).slice(0, 2);
                const centerX = (points[0].x + points[1].x) / 2;
                const centerY = (points[0].y + points[1].y) / 2;
                const distance = Math.hypot(points[1].x - points[0].x, points[1].y - points[0].y);
                const metrics = this.getStageMetrics();

                this.scale = Math.min(
                    this.maxScale,
                    Math.max(1, this.pinchState.scale * (distance / this.pinchState.distance))
                );
                this.translateX = centerX - metrics.centerX - this.pinchState.contentX * this.scale;
                this.translateY = centerY - metrics.centerY - this.pinchState.contentY * this.scale;
            } else if (this.scale > 1) {
                this.translateX += event.clientX - previousPoint.x;
                this.translateY += event.clientY - previousPoint.y;
            }

            this.clampTranslation();
            this.updateTransform();
        }

        onPointerEnd(event) {
            if (!this.pointers.has(event.pointerId)) return;

            this.pointers.delete(event.pointerId);

            try {
                this.stage.releasePointerCapture(event.pointerId);
            } catch (error) {
                // Pointer capture may already be released by the browser.
            }

            if (this.pointers.size >= 2) {
                this.startPinch();
            } else {
                this.pinchState = null;
            }

            if (this.pointers.size === 0) this.stage.classList.remove('is-gesturing');

            this.clampTranslation();
            this.updateTransform();
        }

        onImageLoad() {
            if (!this.hasAttribute('open')) return;
            this.sizePreviewImage();
            this.clampTranslation();
            this.updateTransform();
        }

        onResize() {
            if (!this.hasAttribute('open')) return;
            this.sizePreviewImage();
            this.clampTranslation();
            this.updateTransform();
        }

        onKeydown(event) {
            if (!this.hasAttribute('open')) return;

            if (event.key === 'Escape') {
                event.preventDefault();
                this.closePreview();
                return;
            }

            if (event.key !== 'Tab') return;

            const focusableElements = Array.from(this.modal.querySelectorAll('button:not([disabled])'));
            if (!focusableElements.length) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        }
    }

    customElements.define('tooto-image-preview', TootoImagePreview);
}
